import theme from '@liveauctioneers/caterwaul-components/lib/theme/theme';
import { shortFormatDate } from '@liveauctioneers/caterwaul-components/lib/utils/dates';
import { FormattedMessage } from '@liveauctioneers/hammer-ui-core/intl';
import { BodyPrimary, BodySecondary } from '@liveauctioneers/hammer-ui-core/text';
import * as React from 'react';
import styled from 'styled-components';

import FormattedCurrency from '@/components/Formatted/FormattedCurrency';
import useIsNarrowerThan from '@/hooks/useIsNarrowerThan';

export type WonItemsCardHeaderProps = {
    currency: string;
    date: number;
    invoiceId: number;
    sellerName: string;
    title: string;
    totalPrice: number;
};

const WonItemsCardHeader = ({ currency, date, invoiceId, sellerName, title, totalPrice }: WonItemsCardHeaderProps) => {
    const isMobile = useIsNarrowerThan(theme.breakpoints.tabletNarrowWidthNum + 1);

    const catalogDate = shortFormatDate(date);
    const invoiceTotal = (
        <FormattedMessage
            id="wonitems.catalog.total_label"
            values={{
                amount: (
                    <FormattedCurrency
                        key="invoice-total"
                        sourceCurrency={currency}
                        value={totalPrice}
                    />
                ),
            }}
        />
    );

    return (
        <StyledWonItemsHeader>
            <CatalogDetailsContainer>
                {Boolean(invoiceId) && (
                    <StyledBodySecondary color="secondary">
                        <FormattedMessage
                            id="wonitems.badges.invoice"
                            values={{ invoiceId }}
                        />
                    </StyledBodySecondary>
                )}
                <StyledBodySecondary color="secondary">{catalogDate}</StyledBodySecondary>
                <StyledBodySecondary color="secondary">{title}</StyledBodySecondary>
                <StyledBodySecondary color="secondary">{sellerName}</StyledBodySecondary>
            </CatalogDetailsContainer>
            {Boolean(invoiceId) && (
                <TotalContainer>
                    {!isMobile ? (
                        <BodyPrimary data-testid="totalValue">{invoiceTotal}</BodyPrimary>
                    ) : (
                        <StyledBodySecondary>{invoiceTotal}</StyledBodySecondary>
                    )}
                </TotalContainer>
            )}
        </StyledWonItemsHeader>
    );
};

export default React.memo<WonItemsCardHeaderProps>(WonItemsCardHeader);

const StyledWonItemsHeader = styled.section`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    padding: 16px 24px;
    border-bottom: none;
    color: ${({ theme }) => theme.colors.grey100};
    background-color: ${({ theme }) => theme.colors.grey500};
    min-height: 40px; // ??
    transition: all 0.25s;
    border-top-right-radius: 3px;
    border-top-left-radius: 3px;

    @media (min-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidth}) {
        flex-direction: row;
        justify-content: space-between;
        padding: 16px 32px;
    }
`;

const TotalContainer = styled.section`
    text-align: right;
    white-space: nowrap;
`;

const CatalogDetailsContainer = styled.section`
    display: block;

    @media (min-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidth}) {
        display: flex;
    }
`;

const StyledBodySecondary = styled(BodySecondary)`
    display: inline-block;
    margin-bottom: 4px;
    margin-right: 32px;

    @media (max-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidth}) {
        margin-bottom: 0;
        display: block;
        max-width: 100%;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        margin-right: 0;
        margin-bottom: 4px;
    }
`;
